export const DEFAULT_APPLICATION_LABEL = 'application';

export type ApplicationAction = 'edit' | 'download' | 'getLink';

export type SaveAction = 'saveForLater' | 'saveAndProceed';

export const getApplicationIdentifier = (
  applicationNumber?: string | null,
  applicationId?: string | number | null
): string => {
  const number = applicationNumber?.trim();
  if (number) return number;
  if (applicationId != null && String(applicationId).trim()) return String(applicationId).trim();
  return DEFAULT_APPLICATION_LABEL;
};

export const getApplicationActionAriaLabel = (
  action: ApplicationAction,
  applicationNumber?: string | null,
  applicationId?: string | number | null
): string => {
  const identifier = getApplicationIdentifier(applicationNumber, applicationId);

  switch (action) {
    case 'edit':
      return `Edit application, ${identifier}`;
    case 'download':
      return `Download application, ${identifier}`;
    case 'getLink':
      return `Get link for application, ${identifier}`;
    default:
      return identifier;
  }
};

export const getSaveActionAriaLabel = (
  action: SaveAction,
  options?: {
    stepName?: string | null;
    proceedButtonText?: string | null;
  }
): string => {
  const stepSuffix = options?.stepName?.trim() ? `, ${options.stepName.trim()}` : '';

  if (action === 'saveForLater') {
    return `Save for later${stepSuffix}`;
  }

  const proceedLabel = options?.proceedButtonText?.trim() || 'Save and proceed';
  return `${proceedLabel}${stepSuffix}`;
};

const ESIGN_SIGNATURE_ROLE_LABELS: Record<string, string> = {
  fundmanager: 'Investment Manager',
  trustee: 'Trustee',
  witness: 'Witness',
  investor: 'Investor',
  application: 'Applicant',
  applicant1: '1st Applicant',
  applicant2: '2nd Applicant',
  applicant3: '3rd Applicant',
  authorisedsignatory: 'Authorised Signatory',
  groupsignatory: 'Group Signatory',
  poaapprover: 'POA Approver',
  amcapprover: 'AIF Approver',
  rm: 'RM',
  distributor: 'Distributor',
  subdistributor: "Distributor's RM",
  amcadmin: 'AMC Admin',
};

export const getEsignSignatureRoleLabel = (signatureBy?: string | null): string => {
  const role = signatureBy?.trim();
  if (!role) return '';

  return (
    ESIGN_SIGNATURE_ROLE_LABELS[role] ||
    ESIGN_SIGNATURE_ROLE_LABELS[role.toLowerCase()] ||
    role.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())
  );
};

export const getEsignLinkCopyAriaLabel = (
  name?: string | null,
  signatureBy?: string | null
): string => {
  const signerName = name?.trim() || 'signatory';
  const roleLabel = getEsignSignatureRoleLabel(signatureBy);

  return roleLabel ? `Copy link for ${signerName}, ${roleLabel}` : `Copy link for ${signerName}`;
};
